import type { entities } from "@fraction/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFractionClient } from "./FractionClientContext";

/**
 * The current user's brokerage membership, plus an `updateAssignedBrokerage`
 * mutation that reassigns it. Shared by web + mobile.
 *
 * Pass `enabled` from each platform's auth/role state; pass `userId` to scope the
 * cache per user (web). The mutation optimistically writes the new brokerage into
 * the same cache entry and accepts an optional one-shot `token` (used mid-
 * onboarding before the bearer is fully installed).
 */
export function useBrokerage({ enabled = true, userId }: { enabled?: boolean; userId?: string } = {}) {
  const client = useFractionClient();
  const queryClient = useQueryClient();
  const queryKey = ["brokerage", userId] as const;

  const query = useQuery({
    queryKey,
    enabled,
    queryFn: () => client.getUserBrokerage(),
  });

  const mutation = useMutation({
    mutationFn: async (brokerage: entities.BrokerGroup & { token?: string }) => {
      if (!brokerage?.id) {
        throw new Error("no broker group id");
      }
      if (brokerage.token) {
        client.setBearerToken(brokerage.token);
      }
      queryClient.setQueryData(queryKey, (prev) => ({ ...((prev as object) || {}), brokerGroup: brokerage }));
      await client.updateAssignedBrokerage(brokerage.id);
    },
  });

  return {
    ...query,
    updateAssignedBrokerage: mutation.mutateAsync,
    isUpdatingBrokerage: mutation.isPending,
  };
}
